import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LOCK_CALL_PATH } from '../../routes'
import {
  clipFactoryAPI,
  clipFactoryErrorText,
  type ClipFactoryConfig,
  type LockedCall,
  type ProcessStatus,
} from './clipFactoryApi'
import { SCRIPT_MAX_WORDS, wordCount } from './clipUtils'

type Template = 'call' | 'script'

interface CreateVideoProps {
  config: ClipFactoryConfig | null
  /** A call to start with picked (from "Clip it" on a closed call's Moment). */
  initialCallId?: number | null
  /** A render was queued: open it like an upload. */
  onStarted: (job: ProcessStatus) => void
}

const tabClass = (active: boolean) =>
  `rounded-md px-3 py-1.5 text-sm font-medium ${active ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'}`

/**
 * "Create a video": a 9:16 video made from one of the user's locked calls
 * (Template A) or from a short script (Template B). Clip Factory renders it;
 * every figure on a call video comes from the call's own record.
 */
const CreateVideo = ({ config, initialCallId, onStarted }: CreateVideoProps) => {
  const [template, setTemplate] = useState<Template>('call')
  const [calls, setCalls] = useState<LockedCall[] | null>(null)
  const [callsNote, setCallsNote] = useState<string | null>(null)
  const [callId, setCallId] = useState<number | null>(initialCallId ?? null)
  const [script, setScript] = useState('')
  const [topic, setTopic] = useState('')
  const [voice, setVoice] = useState(false)
  const [drafting, setDrafting] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const maxWords = config?.create?.script_max_words || SCRIPT_MAX_WORDS
  const words = wordCount(script)

  useEffect(() => {
    if (template !== 'call' || calls !== null) return
    let stopped = false
    clipFactoryAPI
      .calls()
      .then((list) => {
        if (stopped) return
        setCalls(list.calls)
        setCallsNote(list.available ? null : list.reason || "Your locked calls can't be read right now.")
      })
      .catch((e) => {
        if (stopped) return
        setCalls([])
        setCallsNote(clipFactoryErrorText(e, "Couldn't load your calls."))
      })
    return () => {
      stopped = true
    }
  }, [template, calls])

  const draft = async () => {
    setError(null)
    setDrafting(true)
    try {
      const result = await clipFactoryAPI.draftScript(topic.trim())
      setScript(result.script)
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't draft a script."))
    } finally {
      setDrafting(false)
    }
  }

  const render = async () => {
    setError(null)
    setStarting(true)
    try {
      const job =
        template === 'call' && callId !== null
          ? await clipFactoryAPI.createRender({ template: 'call', call_id: callId })
          : await clipFactoryAPI.createRender({ template: 'script', script: script.trim(), voice })
      onStarted(job)
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't start the video."))
    } finally {
      setStarting(false)
    }
  }

  const canRender =
    !starting && (template === 'call' ? callId !== null : words > 0 && words <= maxWords)

  return (
    <section className="bg-white rounded-lg shadow p-5" aria-label="Create a video">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Template">
        <button type="button" role="tab" aria-selected={template === 'call'} onClick={() => setTemplate('call')} className={tabClass(template === 'call')}>
          From a locked call
        </button>
        <button type="button" role="tab" aria-selected={template === 'script'} onClick={() => setTemplate('script')} className={tabClass(template === 'script')}>
          From a script
        </button>
      </div>

      {template === 'call' ? (
        <div className="mt-4">
          <p className="text-sm text-gray-600">
            A 9:16 video of about 15 seconds that opens on the result (or "Open"), then the call card, its receipt link
            with a QR code and "Not financial advice". Every value comes from the call's record.
          </p>
          {calls === null ? (
            <p className="mt-3 text-sm text-gray-500">Loading your calls…</p>
          ) : callsNote ? (
            <p className="mt-3 text-sm text-gray-500">{callsNote}</p>
          ) : calls.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">
              You haven&apos;t locked any calls yet.{' '}
              <Link to={LOCK_CALL_PATH} className="font-medium text-primary-600 hover:underline">
                Lock your first call
              </Link>{' '}
              and its video can be made here.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-gray-100" aria-label="Your calls">
              {calls.map((call) => (
                <li key={call.id} className="py-2">
                  <label className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="create-call"
                      className="mt-1 h-4 w-4"
                      checked={callId === call.id}
                      onChange={() => setCallId(call.id)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900">
                        {call.symbol} {call.direction.toUpperCase()} · {call.receipt_path}
                      </span>
                      <span className="block text-xs text-gray-500">
                        Locked {call.locked_at_text} at {call.entry_text} · {call.result_text}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {config?.ai_available && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="A topic, e.g. why I size positions by risk"
                aria-label="Topic"
                maxLength={200}
                className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={draft}
                disabled={!topic.trim() || drafting}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                {drafting ? 'Drafting…' : 'Draft with Claude'}
              </button>
            </div>
          )}
          <label className="block text-sm text-gray-700">
            Script
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              rows={6}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <p className={`text-xs ${words > maxWords ? 'text-danger-600' : 'text-gray-500'}`} data-testid="word-count">
            {words} / {maxWords} words
            {words > maxWords ? ` · shorten it by ${words - maxWords}` : ''}
          </p>
          <p className="text-xs text-gray-500">
            Quoting a result? Give its date range and say "before fees", or the video won't be made.
          </p>
          {config?.create?.voice_available && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="h-4 w-4" checked={voice} onChange={(e) => setVoice(e.target.checked)} />
              Voice-over
            </label>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={render}
        disabled={!canRender}
        className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {starting ? 'Starting…' : 'Make the video'}
      </button>
    </section>
  )
}

export default CreateVideo
